// İSG uzmanı portalı — Kontrol departmanı ↔ sabit-rol eşlemesi (backend `DepartmentRoleMap` AYNASI;
// api-contract §31.4/§31.5).
//
// §31.5 kuyruğu departmanı **echo etmez** ama kapsamlı detay (`?department=`) + onay/ret body'si
// (`{ department }`) departman ister. Çağıranın departman(lar)ı, JWT'deki **rol UUID'lerinden**
// türetilir (`Principal.roleUuids`; auth store). Departman→rol eşlemesi §31'in `DepartmentRoleMap`'i
// ile birebir: **İSG** = isg-expert + isg-manager · **Hekim** = hekim · **İK** = hr-expert.
//
// Rol UUID'leri **deterministiktir** (`DeterministicGuid.Create("role:" + value)` — RFC 4122 v3/MD5,
// sabit namespace `6f9d3a2e-1c47-4b8a-9e21-7c5a0b3d84f6`; ortam-bağımsız aynı — backend seed'inin
// dayandığı özellik). Aşağıdaki değerler o algoritmayla üretilip **hekim = §31.1'deki
// `bbbbca65-74f8-335a-add6-51a3b096214f` ile birebir doğrulanmıştır** (aynı namespace → diğer üçü de
// kesin). Rastgele UUID değil, kataloğun deterministik sabitleridir; hardcode etmek backend seed'iyle
// aynı garantiyi taşır.

import { type PermitApprovalDepartment, useAuthStore } from '@isgportal/shared';

/**
 * Departman → o departmanın sabit-rol UUID'leri (deterministik; `DeterministicGuid.Create`).
 * Doğrulama: `role:hekim` = `bbbbca65-74f8-335a-add6-51a3b096214f` (§31.1 ile birebir).
 */
export const DEPARTMENT_ROLE_UUIDS: Record<PermitApprovalDepartment, string[]> = {
  // isg-expert + isg-manager
  Isg: ['feaf4ec6-fa01-3e12-858e-c4e7a010e62c', 'a6ac0ab0-5520-39dd-b9db-b3a56022c4ba'],
  // hekim
  Hekim: ['bbbbca65-74f8-335a-add6-51a3b096214f'],
  // hr-expert
  Ik: ['4a492787-a50f-3692-9c5c-db2faf52799c'],
};

/** Departman değerlendirme sırası (İSG → Hekim → İK) — türetilen listelerde tutarlı sıra. */
const DEPARTMENT_ORDER: PermitApprovalDepartment[] = ['Isg', 'Hekim', 'Ik'];

/**
 * Verilen rol UUID kümesinden çağıranın Kontrol departman(lar)ını türetir (İSG/Hekim/İK sırasında;
 * yinelenmez). Departman rolü taşımıyorsa `[]`. Case-insensitive karşılaştırma (UUID formatı
 * tutarlı gelse de emniyet için).
 */
export function deriveViewerDepartments(roleUuids: string[]): PermitApprovalDepartment[] {
  const owned = new Set(roleUuids.map((u) => u.toLowerCase()));
  return DEPARTMENT_ORDER.filter((dept) =>
    DEPARTMENT_ROLE_UUIDS[dept].some((roleUuid) => owned.has(roleUuid.toLowerCase())),
  );
}

/**
 * Çağıranın Kontrol departman(lar)ı — auth store'daki principal rol UUID'lerinden türetilir.
 * Tek departmandaysa `[dept]`; birden çok departman rolündeyse çoklu (detay/onay ekranı seçtirir);
 * hiçbiri değilse `[]` (Kontrol menüsü/ekranı görünmez — gate zaten backend'de kapılar).
 */
export function useViewerDepartments(): PermitApprovalDepartment[] {
  const roleUuids = useAuthStore((s) => s.principal?.roleUuids);
  return deriveViewerDepartments(roleUuids ?? []);
}
